// TODO-SERGEY: the types are duplicated in the client and server code. We need to make them common after properly moving to NX.
import { z } from 'zod'

export enum AnalyticsProvider {
	META = 'meta',
	MIXPANEL = 'mixpanel',
}

export enum AnalyticsDevice {
	WEB_BROWSER = 'web-browser',
	MOBILE_APPLICATION = 'mobile-application',
}

export enum ActionName {
	PURCHASE = 'purchase',
	VIEW_ITEM_LIST = 'viewItemList',
	VIEW_ITEM = 'viewItem',
	SELECT_ITEM = 'selectItem',
	ADD_TO_CART = 'addToCart',
	REMOVE_FROM_CART = 'removeFromCart',
	VIEW_CART = 'viewCart',
	BEGIN_CHECKOUT = 'beginCheckout',
	ADD_PAYMENT_INFO = 'addPaymentInfo',
	ADD_SHIPPING_INFO = 'addShippingInfo',
	PAGE_VIEW = 'pageView',
	SELECT_PROMOTION = 'selectPromotion',
	VIEW_PROMOTION = 'viewPromotion',

	// coupon flow
	COUPON_VIEW_DETAILS = 'couponViewDetails',
	COUPON_APPLY_FROM_POPUP = 'couponApplyFromPopup',
	COUPON_APPLY = 'couponApply',
	COUPON_ADD_COUPON = 'couponAddCoupon',
	COUPON_FILTER_COUPONS = 'couponFilterCoupons',
	COUPON_REMOVE_COUPON = 'couponRemoveCoupon',
	DISCOUNT_REDEEM_SUCCESS = 'discountRedeemSuccess',

	// both localization flow
	LOCALIZATION_START = 'localizationStart',
	LOCALIZATION_FINISH = 'localizationFinish',
	LOCALIZATION_SELECT_ORDER_TYPE = 'localizationSelectOrderType',
	
	// old localization flow
	LOCALIZATION_ENTER_ADDRESS = 'localizationEnterAddress',
	LOCALIZATION_LAST_ADDRESS = 'localizationLastAddress',
	LOCALIZATION_USE_MY_LOCATION = 'localizationUseMyLocation',

	// new localization flow
	LOCALIZATION_START_ORDER = 'localizationStartOrder',
	LOCALIZATION_TYPED_ADDRESS_MANUALLY = 'localizationTypedAddressManually',
	LOCALIZATION_CLICKED_LOCATION_ICON = 'localizationClickedLocationIcon',
	LOCALIZATION_SAVED_ADDRESSES = 'localizationSavedAddresses', 
	LOCALIZATION_SHARED_LOCATION_ON_MAP = 'localizationSharedLocationOnMap',
	LOCALIZATION_EDIT_ADDRESS = 'localizationEditAddress',
	LOCALIZATION_CHANGE_ORDER_TYPE = 'localizationChangeOrderType',
	LOCALIZATION_CHANGE_DELIVERY_ADDRESS = 'localizationChangeDeliveryAddress',
    LOCALIZATION_CHANGE_STORE = 'localizationChangeStore',
    LOCALIZATION_ENTER_ADDRESS_MANUALLY = 'localizationEnterAddressManually',
	LOCALIZATION_ADD_NEW_ADDRESS = 'localizationAddNewAddress',
    LOCALIZATION_DELETE_ADDRESS = 'localizationDeleteAddress',
    LOCALIZATION_LOCATION_TYPE = 'localizationLocationType',
	LOCALIZATION_VIEW_HOME_PAGE_MENU = 'localizationViewHomePageMenu',

	SET_USER_PROPERTIES = 'setUserProperties',
	SET_SUPER_PROPERTIES = 'setSuperProperties',
}

export enum ActionDataKey {
	SHIPPING_TIER = 'shipping-tier', // content type
	QUANTITY = 'quantity',
	AFFILIATION = 'affiliation',
	IS_FIRST_PURCHASE = 'is-first-purchase',
	PRODUCT_ITEMS = 'product-items',
	PRODUCT_ITEM_IDS = 'product-item-ids',
	NUMBER_OF_ITEMS = 'number-of-items',
	CHECKOUT_STEP = 'checkout-step',
	COUPON = 'coupon',
	COUPONS = 'coupons',

	USER_ID = 'user-id',
	USER_EMAIL = 'user-email',
	USER_FIRST_NAME = 'user-first-name',
	USER_LAST_NAME = 'user-last-name',
	USER_FULL_NAME = 'user-full-name', // user-first-name + user-last-name
	USER_PHONE_NUMBER = 'user-phone-number',
	USER_STATUS = 'user-status', // signed in / quest

	ORDER_NUMBER = 'id',
	TICTUK_ORDER_ID = 'tictuk-order-id',

	BRAND = 'brand',
	MARKET = 'market',
	LOCALE = 'locale',
	CURRENCY = 'currency',
	COUNTRY_CODE = 'country-code',
	STORE_NAME = 'store-name',
	STORE_ID = 'store-id',
	PREFERRED_LANGUAGE = 'preferred-language',

	PAYMENT_TYPE = 'payment-type',
	REVENUE = 'revenue',
	TAX = 'tax',
	DELIVERY_CHARGE = 'delivery-charge',
	CHARGES = 'charges',

	ITEM_NAME = 'item-name',
	ITEM_ID = 'item-id',
	ITEM_LIST_NAME = 'item-list-name',
	ITEM_QUANTITY = 'item-quantity',
	ITEM_SECTION_TITLE = 'item-section-title',

	PAGE_LOCATION = 'page-location',
	SESSION_CHANNEL = 'session-channel',
	CHANNEL = 'channel',

	TARGET = 'target',

	DISPOSITION = 'disposition',
	PRE_ORDER = 'pre-order',

	COUPON_CODE = 'coupon-code',
	COUPON_NAME = 'coupon-name',
	COUPON_FILTER_TYPE = 'coupon-filter-type'
}

export enum ActionDataTransformation {
	REGEX = 'regex',
	JSON_STRINGIFY = 'jsonStringify',
}

export const transformationsSchema = z.array(
	z.object({
		name: z.nativeEnum(ActionDataTransformation),
		arguments: z.array(z.union([z.string(), z.number()])),
	})
)

export type Transformations = z.infer<typeof transformationsSchema>

export const eventConfigurationSchema = z.array(
	z.object({
		attributeName: z.string(),
		dataKey: z.nativeEnum(ActionDataKey).or(z.string()),
		transformations: transformationsSchema,
	})
)
export type EventConfiguration = z.infer<typeof eventConfigurationSchema>

export const actionConfigurationSchema = z.record(z.string(), eventConfigurationSchema)
export type ActionConfiguration = z.infer<typeof actionConfigurationSchema>

export const providerSchema = z.object({
	name: z.nativeEnum(AnalyticsProvider),
	other: z.any().optional(),
	origin: z.array(z.nativeEnum(AnalyticsDevice)).optional(),
	actions: z.record(z.nativeEnum(ActionName), actionConfigurationSchema),
})
export type ProviderConfiguration = z.infer<typeof providerSchema>

export const configurationSchema = z.object({
	providers: z.array(providerSchema),
})
export type Configuration = z.infer<typeof configurationSchema>

export const actionDataSchemas = {
	[ActionName.PURCHASE]: z.object({
		[ActionDataKey.ORDER_NUMBER]: z.string(),
		[ActionDataKey.TICTUK_ORDER_ID]: z.number(),
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.TAX]: z.string(),
		[ActionDataKey.DELIVERY_CHARGE]: z.string(),
		[ActionDataKey.IS_FIRST_PURCHASE]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PAYMENT_TYPE]: z.string(),
		[ActionDataKey.AFFILIATION]: z.string(),
		[ActionDataKey.USER_EMAIL]: z.string(),
		[ActionDataKey.USER_FIRST_NAME]: z.string(),
		[ActionDataKey.USER_LAST_NAME]: z.string(),
		[ActionDataKey.USER_PHONE_NUMBER]: z.string(),
		[ActionDataKey.USER_STATUS]: z.string(),
		[ActionDataKey.COUPON]: z.string(),
	}),
	[ActionName.VIEW_ITEM]: z.object({
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.ITEM_SECTION_TITLE]: z.string(),
		[ActionDataKey.ITEM_ID]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
	}),
	[ActionName.ADD_TO_CART]: z.object({
		[ActionDataKey.QUANTITY]: z.number(),
		[ActionDataKey.PRODUCT_ITEMS]: z.any(),
		[ActionDataKey.ITEM_SECTION_TITLE]: z.array(z.string()),
		[ActionDataKey.ITEM_NAME]: z.string(),
		[ActionDataKey.ITEM_ID]: z.array(z.string()),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.CHARGES]: z.string(),
		[ActionDataKey.COUPONS]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
	}),
	[ActionName.BEGIN_CHECKOUT]: z.object({
		[ActionDataKey.COUNTRY_CODE]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.STORE_NAME]: z.string(),
		[ActionDataKey.STORE_ID]: z.string(),
		[ActionDataKey.CHECKOUT_STEP]: z.string(),
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PRODUCT_ITEM_IDS]: z.array(z.string()),
		[ActionDataKey.NUMBER_OF_ITEMS]: z.number(),
	}),
	[ActionName.ADD_PAYMENT_INFO]: z.object({
		[ActionDataKey.LOCALE]: z.string(),
		[ActionDataKey.COUNTRY_CODE]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.STORE_NAME]: z.string(),
		[ActionDataKey.STORE_ID]: z.string(),
		[ActionDataKey.CHECKOUT_STEP]: z.string(),
		[ActionDataKey.CHARGES]: z.string(),
		[ActionDataKey.COUPONS]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.NUMBER_OF_ITEMS]: z.number(),
	}),
	[ActionName.VIEW_ITEM_LIST]: z.object({
		[ActionDataKey.ITEM_LIST_NAME]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PRODUCT_ITEM_IDS]: z.array(z.string()),
	}),
	[ActionName.SELECT_PROMOTION]: z.object({
		[ActionDataKey.ITEM_ID]: z.string(),
		[ActionDataKey.ITEM_NAME]: z.string(),
		[ActionDataKey.TARGET]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
	}),
	[ActionName.VIEW_PROMOTION]: z.object({
		[ActionDataKey.ITEM_ID]: z.string(),
		[ActionDataKey.ITEM_NAME]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
	}),
	[ActionName.REMOVE_FROM_CART]: z.object({
		[ActionDataKey.ITEM_ID]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.ITEM_SECTION_TITLE]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
	}),
	[ActionName.VIEW_CART]: z.object({
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PRODUCT_ITEM_IDS]: z.array(z.string()),
		[ActionDataKey.NUMBER_OF_ITEMS]: z.number(),
	}),
	[ActionName.LOCALIZATION_START]: z.object({
		[ActionDataKey.PAGE_LOCATION]: z.string(),
		[ActionDataKey.SESSION_CHANNEL]: z.string(),
	}),
	[ActionName.LOCALIZATION_SELECT_ORDER_TYPE]: z.object({
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.PAGE_LOCATION]: z.string(),
		[ActionDataKey.SESSION_CHANNEL]: z.string(),
	}),
	[ActionName.LOCALIZATION_FINISH]: z.object({
		[ActionDataKey.PAGE_LOCATION]: z.string(),
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.STORE_ID]: z.string(),
	}),
	[ActionName.DISCOUNT_REDEEM_SUCCESS]: z.object({
		[ActionDataKey.COUPON_CODE]: z.string(),
		[ActionDataKey.COUPON_NAME]: z.string(),
		[ActionDataKey.COUPON_FILTER_TYPE]: z.string()
	}),
	[ActionName.COUPON_FILTER_COUPONS]: z.object({
		[ActionDataKey.COUPON_FILTER_TYPE]: z.string()
	}),
	[ActionName.SET_USER_PROPERTIES]: z.object({
		[ActionDataKey.USER_EMAIL]: z.string(),
		[ActionDataKey.USER_FIRST_NAME]: z.string(),
		[ActionDataKey.USER_LAST_NAME]: z.string(),
		[ActionDataKey.USER_FULL_NAME]: z.string(),
		[ActionDataKey.USER_PHONE_NUMBER]: z.string(),
		[ActionDataKey.USER_STATUS]: z.string(),
	}),
	[ActionName.SET_SUPER_PROPERTIES]: z.object({
		[ActionDataKey.MARKET]: z.string(),
		[ActionDataKey.BRAND]: z.string(),
		[ActionDataKey.USER_STATUS]: z.string(),
		[ActionDataKey.SESSION_CHANNEL]: z.string(),
		[ActionDataKey.PREFERRED_LANGUAGE]: z.string(),
		[ActionDataKey.CHANNEL]: z.string()
	}),
}

export type PurchaseAction = z.infer<typeof actionDataSchemas[ActionName.PURCHASE]>
export type ViewItemAction = z.infer<typeof actionDataSchemas[ActionName.VIEW_ITEM]>
export type AddToCartAction = z.infer<typeof actionDataSchemas[ActionName.ADD_TO_CART]>
export type BeginCheckoutAction = z.infer<typeof actionDataSchemas[ActionName.BEGIN_CHECKOUT]>
export type AddPaymentInfoAction = z.infer<typeof actionDataSchemas[ActionName.ADD_PAYMENT_INFO]>
export type ViewItemListAction = z.infer<typeof actionDataSchemas[ActionName.VIEW_ITEM_LIST]>
export type SelectPromotionAction = z.infer<typeof actionDataSchemas[ActionName.SELECT_PROMOTION]>
export type ViewPromotionAction = z.infer<typeof actionDataSchemas[ActionName.VIEW_PROMOTION]>
export type RemoveFromCartAction = z.infer<typeof actionDataSchemas[ActionName.REMOVE_FROM_CART]>
export type ViewCartAction = z.infer<typeof actionDataSchemas[ActionName.VIEW_CART]>
export type LocalizationStartAction = z.infer<typeof actionDataSchemas[ActionName.LOCALIZATION_START]>
export type LocalizationSelectOrderTypeAction = z.infer<typeof actionDataSchemas[ActionName.LOCALIZATION_SELECT_ORDER_TYPE]>
export type LocalizationFinishAction = z.infer<typeof actionDataSchemas[ActionName.LOCALIZATION_FINISH]>
export type CouponAction = z.infer<typeof actionDataSchemas[ActionName.DISCOUNT_REDEEM_SUCCESS]>
export type CouponFilterCouponsAction = z.infer<typeof actionDataSchemas[ActionName.COUPON_FILTER_COUPONS]>
export type SetUserPropertiesAction = z.infer<typeof actionDataSchemas[ActionName.SET_USER_PROPERTIES]>
export type SetSuperPropertiesAction = z.infer<typeof actionDataSchemas[ActionName.SET_SUPER_PROPERTIES]>

export type Action =
	| AddPaymentInfoAction
	| BeginCheckoutAction
	| AddToCartAction
	| ViewItemAction
	| PurchaseAction
	| ViewItemListAction
	| SelectPromotionAction
	| ViewPromotionAction
	| RemoveFromCartAction
	| ViewCartAction
	| LocalizationStartAction
	| LocalizationSelectOrderTypeAction
	| LocalizationFinishAction
	| CouponAction
	| CouponFilterCouponsAction
	| SetUserPropertiesAction
	| SetSuperPropertiesAction
